import React from 'react';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { Box, IconButton, useMediaQuery, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import type { ScrollCarouselProps } from './ScrollCarousel.types';
import { useScrollCarousel } from './useScrollCarousel';

// --- Fade overlay styles ---

const FADE_WIDTH = 64;

const fadeBaseSx = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: FADE_WIDTH,
  pointerEvents: 'none',
  transition: 'opacity 0.2s ease',
  zIndex: 1,
} as const;

// --- Arrow button styles ---

const arrowBaseSx = {
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  bgcolor: 'background.paper',
  boxShadow: 1,
  zIndex: 2,
  '&:hover': { bgcolor: 'background.paper' },
} as const;

export const ScrollCarousel: React.FC<ScrollCarouselProps> = ({
  children,
  gap = 1.5,
  snapAlign = 'start',
  showScrollbar = false,
  showArrows = 'auto',
  showFades = 'auto',
  scrollMode = 'page',
  fadeColor,
  scrollContainerSx,
  sx,
  ...boxProps
}) => {
  const theme = useTheme();
  const hasHover = useMediaQuery('(hover: hover)');
  const { scrollRef, canScrollPrev, canScrollNext, hasOverflow, scrollPrev, scrollNext } =
    useScrollCarousel(scrollMode, snapAlign);

  const resolvedFadeColor = fadeColor ?? theme.palette.background.default;
  const fadesVisible = showFades === 'auto' ? hasOverflow : showFades;
  const arrowsVisible = showArrows === 'auto' ? hasOverflow && hasHover : showArrows && hasHover;

  return (
    <Box
      {...boxProps}
      sx={[
        { position: 'relative' },
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- SxProps array elements are typed as any by MUI
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      {/* Scroll container */}
      <Box
        ref={scrollRef}
        sx={[
          {
            display: 'flex',
            gap,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            ...(!showScrollbar && {
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }),
            ...(snapAlign !== 'none' && {
              '& > *': { scrollSnapAlign: snapAlign },
            }),
          },
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- SxProps array elements are typed as any by MUI
          ...(Array.isArray(scrollContainerSx)
            ? scrollContainerSx
            : scrollContainerSx
              ? [scrollContainerSx]
              : []),
        ]}
      >
        {children}
      </Box>

      {/* Edge fade gradients — all devices */}
      {fadesVisible && (
        <>
          <Box
            sx={{
              ...fadeBaseSx,
              left: 0,
              background: `linear-gradient(to right, ${resolvedFadeColor} 0%, ${alpha(resolvedFadeColor, 0.7)} 40%, ${alpha(resolvedFadeColor, 0.15)} 75%, transparent 100%)`,
              opacity: canScrollPrev ? 1 : 0,
            }}
          />
          <Box
            sx={{
              ...fadeBaseSx,
              right: 0,
              background: `linear-gradient(to left, ${resolvedFadeColor} 0%, ${alpha(resolvedFadeColor, 0.7)} 40%, ${alpha(resolvedFadeColor, 0.15)} 75%, transparent 100%)`,
              opacity: canScrollNext ? 1 : 0,
            }}
          />
        </>
      )}

      {/* Arrow buttons — hover-capable devices only */}
      {arrowsVisible && canScrollPrev && (
        <IconButton
          size="small"
          onClick={scrollPrev}
          aria-label="Scroll left"
          sx={{ ...arrowBaseSx, left: 16 }}
        >
          <ChevronLeft />
        </IconButton>
      )}
      {arrowsVisible && canScrollNext && (
        <IconButton
          size="small"
          onClick={scrollNext}
          aria-label="Scroll right"
          sx={{ ...arrowBaseSx, right: 16 }}
        >
          <ChevronRight />
        </IconButton>
      )}
    </Box>
  );
};
